import html2canvas from "html2canvas";

export const BOOKING_POSTER_WIDTH = 414;
const CAPTURE_SCALE = 2;

const MAIN_FONT_SIZES = [11, 12, 14, 15, 26];

function sanitizeFilename(name) {
    return (
        String(name || "booking")
            .trim()
            .replace(/[^\w\u4e00-\u9fff\- ]+/g, "")
            .replace(/\s+/g, "-")
            .slice(0, 60) || "booking"
    );
}

function getMainFontsUrl() {
    const base = process.env.PUBLIC_URL || "";
    const prefix = base.endsWith("/") ? base.slice(0, -1) : base;
    return `${window.location.origin}${prefix}/fonts/1.ttf`;
}

export async function ensureBookingPosterFontsLoaded() {
    if (!document.fonts?.load) {
        return;
    }

    try {
        await Promise.race([
            Promise.all(MAIN_FONT_SIZES.map((size) => document.fonts.load(`${size}px MainFonts`))),
            document.fonts.ready,
            new Promise((resolve) => {
                window.setTimeout(resolve, 2500);
            }),
        ]);
    } catch {
        // Fallback stack will still render if custom font is unavailable.
    }
}

function prepareCloneForCapture(clonedDoc, clonedElement) {
    const style = clonedDoc.createElement("style");
    style.textContent = `
        @font-face {
            font-family: MainFonts;
            src: url("${getMainFontsUrl()}") format("truetype");
        }
        .booking-poster {
            width: ${BOOKING_POSTER_WIDTH}px !important;
            max-width: none !important;
            aspect-ratio: unset !important;
            margin: 0 !important;
            box-sizing: border-box !important;
        }
        .booking-poster__inner {
            padding: 0 34px !important;
            box-sizing: border-box !important;
        }
        .booking-poster__brand,
        .booking-poster__doc-title,
        .booking-poster__project,
        .booking-poster__row-label,
        .booking-poster__website {
            font-family: MainFonts, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif !important;
        }
        .booking-poster__brand {
            padding-left: 0.45em;
        }
        .booking-poster__website {
            padding-left: 0.35em;
        }
    `;
    clonedDoc.head.appendChild(style);

    if (clonedElement) {
        clonedElement.style.width = `${BOOKING_POSTER_WIDTH}px`;
        clonedElement.style.maxWidth = "none";
        clonedElement.style.aspectRatio = "unset";
        clonedElement.style.margin = "0";
    }
}

function applyCaptureDimensions(element) {
    element.style.width = `${BOOKING_POSTER_WIDTH}px`;
    element.style.maxWidth = `${BOOKING_POSTER_WIDTH}px`;
    element.style.aspectRatio = "unset";
    element.style.margin = "0";
    element.style.boxSizing = "border-box";
}

function restoreCaptureDimensions(element, previous) {
    element.style.width = previous.width;
    element.style.maxWidth = previous.maxWidth;
    element.style.aspectRatio = previous.aspectRatio;
    element.style.margin = previous.margin;
    element.style.boxSizing = previous.boxSizing;
}

export async function captureBookingPoster(element) {
    if (!element) {
        throw new Error("Booking poster element is missing");
    }

    await ensureBookingPosterFontsLoaded();

    const previous = {
        width: element.style.width,
        maxWidth: element.style.maxWidth,
        aspectRatio: element.style.aspectRatio,
        margin: element.style.margin,
        boxSizing: element.style.boxSizing,
    };

    applyCaptureDimensions(element);

    try {
        await new Promise((resolve) => {
            requestAnimationFrame(() => requestAnimationFrame(resolve));
        });

        const canvas = await html2canvas(element, {
            scale: CAPTURE_SCALE,
            backgroundColor: "#fbfbfd",
            useCORS: true,
            logging: false,
            scrollX: 0,
            scrollY: 0,
            windowWidth: BOOKING_POSTER_WIDTH,
            onclone: prepareCloneForCapture,
        });

        return canvas;
    } finally {
        restoreCaptureDimensions(element, previous);
    }
}

export async function getBookingPosterImage(element, title) {
    const canvas = await captureBookingPoster(element);
    const filename = `${sanitizeFilename(title)}-booking.png`;
    return { dataUrl: canvas.toDataURL("image/png"), filename };
}

export async function downloadBookingPosterFromElement(element, title) {
    const { dataUrl, filename } = await getBookingPosterImage(element, title);
    const link = document.createElement("a");
    link.download = filename;
    link.href = dataUrl;
    link.click();
}
